import { api, apiStatic, getCookies } from 'AurionCR/components';
import axios from 'axios';
import React, { ReactNode, useEffect, useState } from 'react';
import { API_AUTH } from 'services/account';

let isRefreshing = false;
let refreshingCall: Promise<any>;

interface Props {
  onRefresh: () => Promise<{ token: string; refreshToken: string; expires: string }>;
  onSuccess: (payload: { token: string; refreshToken: string; expires: string }) => void;
  onError: (error: Error) => void;
  children: ReactNode;
}

const instances = [apiStatic, api, axios];

export const AxiosInterceptorsRefreshToken: React.FC<Props> = ({
  children,
  onRefresh,
  onSuccess,
  onError,
}) => {
  const [canRender, setCanRender] = useState(false);

  useEffect(() => {
    setCanRender(true);

    const IDS = instances.map((instance) => {
      return instance.interceptors.response.use(
        (response) => {
          // If the response comes from API_AUTH.CHECK_AUTH endpoint
          // and the server returns 'not-authorize' (200 OK),
          // we artificially create an Error with status 401
          // so that it can be handled in the error block below.
          if (
            response.config.url === API_AUTH.CHECK_AUTH && // Adjust this to your actual endpoint
            response.data === 'not-authorize' &&
            !isRefreshing
          ) {
            // Retrieve tokens from cookies
            const Token = getCookies(window.authCookieName_);
            const RefreshToken = getCookies(window.authRefreshCookieName_);

            if (Token && RefreshToken) {
              refreshingCall = onRefresh()
                .then((res) => {
                  isRefreshing = false;
                  const { token, refreshToken, expires } = res;
                  // Call the onSuccess handler to store new tokens (e.g. in cookies or Redux)
                  onSuccess({ token, refreshToken, expires });
                })
                .catch((err) => {
                  isRefreshing = false;
                  const errorStatus = err.response ? err.response.status : null;

                  // If we got a 401 again, this usually means that even after refresh,
                  // our tokens are invalid - so handle it accordingly
                  if (errorStatus === 401) {
                    onError(err);
                  }
                  return Promise.reject(err);
                });
            } else {
              onError(new Error('Received "not-authorize" from CHECK_AUTH'));
            }
          }

          // Otherwise, return the response as normal
          return response;
        },
        (error) => {
          // Determine if this is a 401 error
          const status = error.response ? error.response.status : null;

          // Retrieve tokens from cookies
          const Token = getCookies(window.authCookieName_);
          const RefreshToken = getCookies(window.authRefreshCookieName_);

          // If it's not a 401, or tokens are missing, or there's some special 401 data,
          // then we just reject the error.
          if (
            status !== 401 ||
            !(Token && RefreshToken) ||
            // If you have a special "not-enough-permissions" response or similar,
            // you can handle it here. For example:
            (status === 401 && error?.response?.data)
          ) {
            return Promise.reject(error);
          }

          // If a token refresh is already in progress, wait for it to complete
          // and then retry the original request
          if (isRefreshing) {
            return refreshingCall.then(() => instance(error.config));
          }

          // Otherwise, start the refresh process
          isRefreshing = true;
          refreshingCall = onRefresh()
            .then((res) => {
              isRefreshing = false;
              const { token, refreshToken, expires } = res;

              // Call the onSuccess handler to store new tokens (e.g. in cookies or Redux)
              onSuccess({ token, refreshToken, expires });

              // Retry the original request with the refreshed token
              return instance(error.config);
            })
            .catch((err) => {
              isRefreshing = false;
              const errorStatus = err.response ? err.response.status : null;

              // If we got a 401 again, this usually means that even after refresh,
              // our tokens are invalid - so handle it accordingly
              if (errorStatus === 401) {
                onError(err);
              }
              return Promise.reject(err);
            });

          return refreshingCall;
        },
      );
    });

    // Eject all interceptors on unmount
    return () => {
      instances.forEach((instance, i) => {
        instance.interceptors.response.eject(IDS[i]);
      });
    };
  }, [onRefresh, onSuccess, onError, setCanRender]);

  return <>{canRender ? children : null}</>;
};
